/**
 * Shared utilities for bowling block-summary charts.
 *
 * Houses pure data helpers, styling constants, and React hooks that have no
 * JSX dependency. JSX-producing scaffold pieces live in `ChartScaffold.tsx`.
 */
import { DateTime } from 'luxon';

/** Acute (short-term) load window for ACWR and 7-day rolling averages. */
export const ACUTE_WINDOW_DAYS = 7;
/** Chronic (long-term) load window for ACWR and 28-day rolling averages. */
export const CHRONIC_WINDOW_DAYS = 28;
/** Extended chronic load window for the 70-day (10-week) rolling average. */
export const TEN_WEEK_WINDOW_DAYS = 70;

/**
 * Expands a sparse map of ISO-date → T into a contiguous array from the
 * earliest key to the latest, filling any missing days with makeDefault.
 *
 * Charts require a value for every calendar day in the range so that
 * Recharts renders gaps as zero rather than skipping the date entirely.
 */
export function buildContiguousDays<T extends { day: string }>(
  byDay: Map<string, T>,
  makeDefault: (day: string) => T,
): T[] {
  if (byDay.size === 0) return [];
  const dates = Array.from(byDay.keys()).sort();
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  if (!firstDate || !lastDate) return [];
  const start = DateTime.fromISO(firstDate);
  const end = DateTime.fromISO(lastDate);
  if (!start.isValid || !end.isValid) return [];
  const result: T[] = [];
  for (let d = start; d <= end; d = d.plus({ days: 1 })) {
    const iso = d.toISODate() ?? '';
    result.push(byDay.get(iso) ?? makeDefault(iso));
  }
  return result;
}

const SHORT_RANGE_MAX_DAYS = 14;
const MEDIUM_RANGE_MAX_DAYS = 31;

/**
 * Returns the Recharts XAxis `interval` prop for a given number of data points.
 *
 * Keeps tick density readable across short (≤14 days), medium (≤31 days),
 * and long date ranges without hardcoding a value in each chart component.
 *
 * - ≤14 → 0 (show every tick)
 * - ≤31 → 6 (roughly weekly)
 * - else → ceil(n/10) (~10 evenly-spaced ticks)
 */
export function xAxisTickInterval(dataLength: number): number {
  if (dataLength <= SHORT_RANGE_MAX_DAYS) return 0;
  if (dataLength <= MEDIUM_RANGE_MAX_DAYS) return 6;
  return Math.ceil(dataLength / 10);
}

/**
 * Truncates `text` to fit a pixel `maxWidth` for the given font size, ending
 * with an ellipsis if any characters were dropped. Uses a `fontSize * 0.6`
 * heuristic for average char width — sufficient for short phase labels in
 * the bowling charts, where exact metrics are not worth canvas-measuring.
 */
export function truncateToWidth(
  text: string,
  maxWidth: number,
  fontSize: number,
): string {
  if (maxWidth <= 0) return '';
  const approxCharWidth = fontSize * 0.6;
  const maxChars = Math.floor(maxWidth / approxCharWidth);
  if (text.length <= maxChars) return text;
  if (maxChars <= 1) return '…';
  return text.slice(0, maxChars - 1) + '…';
}

export const PHASE_BAND_FILL_OPACITY = 0.15;
export const PHASE_LABEL_FONT_SIZE = 10;
export const PHASE_LABEL_FILL = '#6b7280';
export const PHASE_LABEL_INSET = 10;
export const TODAY_LINE_COLOR = '#1976d2';

/**
 * Today's ISO date plus whether it falls inside `data`'s day range. Returns
 * a stable shape so callers can branch on `showTodayLine` without recomputing.
 */
export function computeTodayLine(data: Array<{ day: string }>): {
  todayIso: string | null;
  showTodayLine: boolean;
} {
  const todayIso = DateTime.now().toISODate();
  const firstDay = data[0]?.day;
  const lastDay = data[data.length - 1]?.day;
  const showTodayLine =
    todayIso != null &&
    firstDay != null &&
    lastDay != null &&
    todayIso >= firstDay &&
    todayIso <= lastDay;
  return { todayIso, showTodayLine };
}
